import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getProductionStatus } from "../../api/reports";
import { setProductionOrderProblem } from "../../api/productionOrders";
import { EmptyState, FilterChips, FormDialog, PageHeader, StatusBadge } from "../../components/ui";

function fmtDateTime(val) {
  if (!val) return "—";
  return String(val).replace("T", " ").slice(0, 19);
}

function fmtDate(val) {
  if (!val) return "—";
  const s = String(val);
  // Accept either YYYY-MM-DD or ISO date-time
  return s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
}

const FILTERS = [
  { key: "tumu", label: "Tümü" },
  { key: "beklemede", label: "Beklemede" },
  { key: "baskida", label: "Baskıda" },
  { key: "kesimde", label: "Kesimde" },
  { key: "ayiklamada", label: "Ayıklamada" },
  { key: "formlamada", label: "Formlamada" },
  { key: "shrinkte", label: "Shrink" },
  { key: "tamamlandi", label: "Tamamlandı" },
  { key: "problemli", label: "Problemli" },
];

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const d = new Date(String(dueDate).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return d < t;
}

// İş emri no'sunun üzerine gelince ürün ve varyantı adlandırılmış olarak gösterir.
// (İş emirleri / siparişler sayfalarındaki tooltip ile aynı desen.)
function orderNoCell(row) {
  const details = Array.isArray(row?.variant_details) ? row.variant_details : [];
  // product_name parantez içinde ham varyant dizisini taşıyor; temiz malzeme adını kullan.
  const productName = row?.product_material_name || row?.product_name || "";
  const number = (
    <Typography component="span" sx={{ fontWeight: 800 }}>{row?.order_no || "—"}</Typography>
  );
  if (!details.length && !productName) return number;

  return (
    <Tooltip
      arrow
      placement="right"
      title={
        <div>
          {productName ? (
            <div style={{ fontWeight: 700, marginBottom: 6 }}>{productName}</div>
          ) : null}
          {details.map((detail, index) => (
            <div key={`${detail?.label || "dim"}-${index}`} style={{ whiteSpace: "nowrap" }}>
              <span style={{ opacity: 0.75 }}>{detail?.label}:</span>{" "}
              <b>{detail?.value}{detail?.unit ? ` ${detail.unit}` : ""}</b>
            </div>
          ))}
        </div>
      }
    >
      <span style={{ cursor: "help", textDecoration: "underline dotted" }}>{number}</span>
    </Tooltip>
  );
}

export default function ProductionStatusReportPage() {
  const [stage, setStage] = useState("tumu");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);

  const [problemModalOpen, setProblemModalOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [problemNote, setProblemNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [pendingProblemValue, setPendingProblemValue] = useState(false);

  const stageParam = stage === "tumu" ? null : stage;

  const title = useMemo(() => {
    const f = FILTERS.find((x) => x.key === stage);
    return f ? `Üretim Durum Raporu — ${f.label}` : "Üretim Durum Raporu";
  }, [stage]);

  async function load({ stageKey = stage, searchText = search } = {}) {
    setError("");
    setLoading(true);
    try {
      const s = stageKey === "tumu" ? null : stageKey;
      const q = String(searchText || "").trim();
      const res = await getProductionStatus({
        ...(s ? { stage: s } : {}),
        ...(q ? { search: q } : {}),
      });
      setItems(Array.isArray(res) ? res : []);
    } catch (err) {
      console.error("PRODUCTION STATUS LOAD ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setError(`Rapor alınamadı${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ stageKey: stage, searchText: search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageParam]);

  function openProblemModal(row) {
    setActiveRow(row);
    setProblemNote(row?.problem_note ? String(row.problem_note) : "");
    setSaveError("");
    setPendingProblemValue(true);
    setProblemModalOpen(true);
  }

  function closeProblemModal() {
    setProblemModalOpen(false);
    setActiveRow(null);
    setProblemNote("");
    setSaveError("");
    setPendingProblemValue(false);
  }

  async function clearProblem(row) {
    if (!row?.production_order_id) return;
    setSaveError("");
    setSaving(true);
    try {
      await setProductionOrderProblem(row.production_order_id, { problem_flag: false, problem_note: null });
      await load({ stageKey: stage, searchText: search });
    } catch (err) {
      console.error("PROBLEM CLEAR ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setSaveError(`Güncelleme başarısız${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  async function saveProblem() {
    if (!activeRow?.production_order_id) return;
    setSaveError("");
    setSaving(true);
    try {
      const note = String(problemNote || "").trim();
      await setProductionOrderProblem(activeRow.production_order_id, {
        problem_flag: pendingProblemValue ? true : false,
        problem_note: note ? note : null,
      });
      closeProblemModal();
      await load({ stageKey: stage, searchText: search });
    } catch (err) {
      console.error("PROBLEM SAVE ERROR", err);
      const status = err?.response?.status;
      const data = err?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        null;
      setSaveError(`Güncelleme başarısız${status ? ` (HTTP ${status})` : ""}${message ? `: ${message}` : ""}`);
    } finally {
      setSaving(false);
    }
  }

  const stats = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const total = list.length;
    const problem = list.filter((x) => Number(x.problem_flag) === 1).length;
    const completed = list.filter((x) => String(x.stage || "").toLowerCase() === "tamamlandi").length;
    const overdue = list.filter((x) => isOverdue(x.due_date) && String(x.stage || "").toLowerCase() !== "tamamlandi").length;
    return { total, problem, completed, overdue };
  }, [items]);

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1540, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
      <PageHeader
        title={title}
        subtitle="Filtre ve arama ile tüm iş emirlerini proses durumuna göre görüntüleyin."
        actions={
          <Stack
            component="form"
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            onSubmit={(e) => {
              e.preventDefault();
              load({ stageKey: stage, searchText: search });
            }}
            sx={{ width: { xs: "100%", md: "auto" }, p: 1, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 8px 24px rgba(79, 70, 229, 0.08)" }}
          >
            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara: İş emri no / müşteri / ürün / sipariş no"
              sx={{ minWidth: { xs: "100%", sm: 360 } }}
            />
            <Button type="submit" variant="contained" disabled={loading} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", boxShadow: "none", "&:hover": { bgcolor: "#3730a3", boxShadow: "none" } }}>
              Ara
            </Button>
          </Stack>
        }
      />

      <Card elevation={0} sx={{ borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
        <CardContent sx={{ py: 2.25, px: { xs: 2, md: 2.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ flexGrow: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Toplam
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {stats.total}
                </Typography>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Termin geçmiş
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {stats.overdue}
                </Typography>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Problemli
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {stats.problem}
                </Typography>
              </Box>
              <Divider flexItem orientation="vertical" sx={{ display: { xs: "none", md: "block" } }} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Tamamlandı
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {stats.completed}
                </Typography>
              </Box>
            </Stack>

            <FilterChips options={FILTERS} value={stage} onChange={setStage} />
          </Stack>
        </CardContent>
        {loading ? <LinearProgress /> : null}
      </Card>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {saveError ? <Alert severity="error">{saveError}</Alert> : null}

      {!loading && (!items || items.length === 0) ? (
        <EmptyState description="Seçili filtre ve arama kriterine uygun kayıt bulunamadı." />
      ) : (
        <Paper elevation={0} sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 260px)", bgcolor: "#fff", borderRadius: 3, border: "1px solid #ddd6fe" }}>
            <Table stickyHeader size="small" sx={{ "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", fontWeight: 800, borderColor: "#ddd6fe" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb" }, "& .MuiTableBody-root .MuiTableRow-root:hover": { bgcolor: "#fafaff" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Müşteri Bilgisi</TableCell>
                  <TableCell>İş Emri No</TableCell>
                  <TableCell>Sipariş Tarihi</TableCell>
                  <TableCell>Termin Tarihi</TableCell>
                  <TableCell>Proses / Durum</TableCell>
                  <TableCell>Problemli</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(items || []).map((row, idx) => {
                  const isProblem = Number(row.problem_flag) === 1;
                  const salesLabel = row.sales_order_no ? `${row.sales_order_no}` : "—";
                  const overdue = isOverdue(row.due_date) && String(row.stage || "").toLowerCase() !== "tamamlandi";

                  return (
                    <TableRow
                      key={row.production_order_id}
                      hover
                      sx={{
                        bgcolor: isProblem ? "rgba(251, 146, 60, 0.12)" : idx % 2 ? "rgba(15, 23, 42, 0.02)" : "transparent",
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{row.customer_name || "—"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sipariş: {salesLabel}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        {orderNoCell(row)}
                        <Typography variant="body2" color="text.secondary">
                          Status: {row.status || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell>{fmtDateTime(row.sales_order_created_at)}</TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: overdue ? 800 : 500, color: overdue ? "error.main" : "text.primary" }}>
                            {fmtDate(row.due_date)}
                          </Typography>
                          {overdue ? <StatusBadge value="problemli" label="Termin" /> : null}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                            <StatusBadge value={row.stage || "—"} />
                            {isProblem ? <StatusBadge value="problemli" label="Problemli" /> : null}
                          </Stack>
                          {row.stage_work_center_code || row.stage_work_center_name ? (
                            <Typography variant="body2" color="text.secondary">
                              {row.stage_work_center_code || ""} {row.stage_work_center_name || ""}
                              {row.stage_op_no ? ` • OP ${row.stage_op_no}` : ""}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                          <Switch
                            checked={isProblem}
                            disabled={saving}
                            onChange={(_, checked) => {
                              if (checked) {
                                setPendingProblemValue(true);
                                openProblemModal(row);
                              } else {
                                clearProblem(row);
                              }
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!isProblem || saving}
                            onClick={() => {
                              setPendingProblemValue(true);
                              openProblemModal(row);
                            }}
                          >
                            Not
                          </Button>
                        </Stack>
                        {isProblem && row.problem_note ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {String(row.problem_note)}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <FormDialog
        open={problemModalOpen}
        paperSx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" }}
        titleSx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}
        titleTextSx={{ color: "#fff" }}
        subtitleTextSx={{ color: "rgba(255,255,255,0.80)" }}
        contentSx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc", borderColor: "#e0e7ff", "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}
        actionsSx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e0e7ff", "& .MuiButton": { borderRadius: 2, textTransform: "none", fontWeight: 800 }, "& .MuiButton-contained:not(.Mui-disabled)": { bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } } }}
        title="Problemli İşaretle"
        subtitle={`${activeRow?.order_no || ""}${activeRow?.customer_name ? ` • ${activeRow.customer_name}` : ""}`}
        onClose={closeProblemModal}
        onSubmit={saveProblem}
        submitLabel={saving ? "Kaydediliyor..." : "Kaydet"}
        loading={saving}
      >
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Problemli durumu
            </Typography>
            <Switch
              checked={pendingProblemValue}
              disabled={saving}
              onChange={(_, checked) => setPendingProblemValue(checked)}
            />
          </Stack>

          <TextField
            label="Not (opsiyonel)"
            value={problemNote}
            onChange={(e) => setProblemNote(e.target.value)}
            placeholder="Problem detayı, takip notu..."
            multiline
            minRows={4}
            disabled={saving}
          />

          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        </Stack>
      </FormDialog>
    </Stack>
  );
}

